import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Box,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useTranslation } from 'react-i18next';
import { formatHour } from '@/utils/formatters';

const fallbackDaysOfWeek = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
};

const EnergyAnalyzerFilterDialog = ({
  open,
  handleClose,
  handleApply,
  selectedEnergyGroup,
  startHour,
  endHour,
}) => {
  const [hoursFilter, setHoursFilter] = useState(undefined);
  const [daysFilter, setDaysFilter] = useState(undefined);
  const [holidaysFilter, setHolidaysFilter] = useState(undefined);
  const { t } = useTranslation('energyxplorer-analysis-tabs');

  const daysOfWeek =
    selectedEnergyGroup?.options?.daysOfWeek ?? fallbackDaysOfWeek;

  const handleSubmit = () => {
    handleApply({
      activeHoursFilter: hoursFilter,
      activeDaysFilter: daysFilter,
      activeHolidaysFilter: holidaysFilter,
    });
    handleClose();
  };

  const onClose = () => {
    setHoursFilter(undefined);
    setDaysFilter(undefined);
    setHolidaysFilter(undefined);
    handleClose();
  };

  const renderToggleGroup = (label, value, setValue, options) => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <ToggleButtonGroup
        value={stringify(value)}
        exclusive
        onChange={(_, newValue) => setValue(parseNullable(newValue))}
        fullWidth
        color="primary"
      >
        {options.map(({ label, value }) => (
          <ToggleButton
            key={value}
            value={value}
            sx={{ textTransform: 'none', flex: 1, borderRadius: 3 }}
          >
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('child_analysis.add_filtered_series')}</DialogTitle>
      <DialogContent dividers>
        {renderToggleGroup(
          t('filter_labels.hours'),
          hoursFilter,
          setHoursFilter,
          [
            // { label: t('filter_labels.no_filter_applied'), value: '' },
            { label: 'Alle', value: '' },
            {
              label: `${formatHour(startHour)} - ${formatHour(endHour)}`,
              value: 'true',
            },
            {
              label: `${formatHour(endHour)} - ${formatHour(startHour)}`,
              value: 'false',
            },
          ],
        )}

        {renderToggleGroup(t('filter_labels.days'), daysFilter, setDaysFilter, [
          // { label: t('filter_labels.no_filter_applied'), value: '' },
          { label: 'Alle', value: '' },
          {
            label: (
              <Box
                sx={{
                  display: 'flex',
                  gap: 0,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {[
                  { key: 'monday', label: 'M' },
                  { key: 'tuesday', label: 'T' },
                  { key: 'wednesday', label: 'O' },
                  { key: 'thursday', label: 'T' },
                  { key: 'friday', label: 'F' },
                  { key: 'saturday', label: 'L' },
                  { key: 'sunday', label: 'S' },
                ].map(({ key, label }) => (
                  <ToggleButton
                    key={key}
                    value={key}
                    disabled={true}
                    selected={daysOfWeek[key]} // for the first group
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: 0,
                      fontWeight: 'bold',
                      fontSize: '0.60rem',
                      p: 0,
                      '&.Mui-selected': {
                        backgroundColor: 'gray',
                        color: 'white',
                      },
                    }}
                  >
                    {label}
                  </ToggleButton>
                ))}
              </Box>
            ),
            value: 'true',
          },
          {
            label: (
              <Box
                sx={{
                  display: 'flex',
                  gap: 0,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {[
                  { key: 'monday', label: 'M' },
                  { key: 'tuesday', label: 'T' },
                  { key: 'wednesday', label: 'O' },
                  { key: 'thursday', label: 'T' },
                  { key: 'friday', label: 'F' },
                  { key: 'saturday', label: 'L' },
                  { key: 'sunday', label: 'S' },
                ].map(({ key, label }) => (
                  <ToggleButton
                    key={key}
                    value={key}
                    disabled={true}
                    selected={!daysOfWeek[key]} // for the second group
                    // onChange={() => handleDayToggle(key)}
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: 0,
                      fontWeight: 'bold',
                      fontSize: '0.60rem',
                      p: 0,
                      '&.Mui-selected': {
                        backgroundColor: 'gray',
                        color: 'white',
                        // '&:hover': {
                        //   backgroundColor: darken('#4CAF50', 0.1),
                        // },
                      },
                    }}
                  >
                    {label}
                  </ToggleButton>
                ))}
              </Box>
            ),
            value: 'false',
          },
        ])}

        {renderToggleGroup(
          t('filter_labels.holidays'),
          holidaysFilter,
          setHolidaysFilter,
          [
            // { label: t('filter_labels.no_filter_applied'), value: '' },
            { label: 'Alle', value: '' },
            { label: 'Kun helligdage', value: 'true' },
            { label: 'Ingen helligdage', value: 'false' },
          ],
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button variant={'text'} onClick={handleClose}>
          {t('buttonLabel.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant={'contained'}
          endIcon={<AddRoundedIcon />}
        >
          {t('buttonLabel.add_filtered_series')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const parseNullable = (val) => {
  if (val === 'true') return true;
  if (val === 'false') return false;
  return undefined;
};

const stringify = (val) => {
  if (val === true) return 'true';
  if (val === false) return 'false';
  return '';
};

export default EnergyAnalyzerFilterDialog;
